import IconButton from '../IconButton/IconButton'
import InputNumber from '../InputNumber/InputNumber'
import Paragraph from '../Paragraph/Paragraph'
import classes from './PaginationControl.module.scss'
import {
  HiChevronDoubleLeft,
  HiChevronDoubleRight,
  HiChevronLeft,
  HiChevronRight
} from 'react-icons/hi'
import {
  useEffect,
  useState
} from 'react'
import type { KeyboardEvent } from 'react'

export interface Props {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  isDisabled?: boolean
}

const PaginationControl = ({
  currentPage,
  totalPages,
  onPageChange,
  isDisabled = false
}: Props) => {
  const isFirstPage = currentPage <= 1
  const isLastPage = currentPage >= totalPages

  const [draft, setDraft] = useState<number | undefined>(currentPage)

  useEffect(() => {
    setDraft(currentPage)
  }, [currentPage])

  const clampPage = (page: number) => Math.min(Math.max(page, 1), totalPages)

  const goToPage = (page: number) => {
    const clamped = clampPage(page)

    if (clamped !== currentPage) onPageChange(clamped)
  }

  const commitPage = (value: number | undefined) => {
    if (value === undefined) return

    const clamped = clampPage(value)

    setDraft(clamped)

    if (clamped !== currentPage) onPageChange(clamped)
  }

  const handleInputChange = (value: number) => {
    if (!Number.isFinite(value)) {
      setDraft(undefined)

      return
    }

    setDraft(clampPage(value))
  }

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // type='number' natively blocks most letters but still allows e/E (scientific
    // notation), +, -, and . — none of which are valid for a page number.
    if (['e', 'E', '+', '-', '.'].includes(event.key)) {
      event.preventDefault()

      return
    }

    if (event.key === 'Enter') commitPage(draft)
  }

  const handleInputBlur = () => {
    if (draft === undefined) {
      setDraft(currentPage)
    } else {
      commitPage(draft)
    }
  }

  const handleFirst = () => { goToPage(1) }
  const handlePrevious = () => { goToPage(currentPage - 1) }
  const handleNext = () => { goToPage(currentPage + 1) }
  const handleLast = () => { goToPage(totalPages) }

  return (
    <div className={classes.PaginationControl}>
      <IconButton
        isDisabled={isDisabled || isFirstPage}
        onClick={handleFirst}
        size='sm'
        title='First page'
      >
        <HiChevronDoubleLeft aria-hidden />
      </IconButton>
      <IconButton
        isDisabled={isDisabled || isFirstPage}
        onClick={handlePrevious}
        size='sm'
        title='Previous page'
      >
        <HiChevronLeft aria-hidden />
      </IconButton>
      <div className={classes.pageInputGroup}>
        <InputNumber
          isDisabled={isDisabled}
          isEmptyValueAllowed
          label='Page number'
          max={totalPages}
          min={1}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          size='sm'
          value={draft}
        />
        <Paragraph>
          / {totalPages}
        </Paragraph>
      </div>
      <IconButton
        isDisabled={isDisabled || isLastPage}
        onClick={handleNext}
        size='sm'
        title='Next page'
      >
        <HiChevronRight aria-hidden />
      </IconButton>
      <IconButton
        isDisabled={isDisabled || isLastPage}
        onClick={handleLast}
        size='sm'
        title='Last page'
      >
        <HiChevronDoubleRight aria-hidden />
      </IconButton>
    </div>
  )
}

export default PaginationControl
